import * as React from "react";
import SearchIcon from "@mui/icons-material/Search";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemButton from "@mui/material/ListItemButton";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Skeleton from "@mui/material/Skeleton";
import { Suggestion } from "./SearchBar";
import {
  AuthorSuggestion,
  NewsFeedResource,
  PerformedSearchSuggestion,
  TitleSuggestion,
} from "../dto/SuggestionResult";
import { CoverSize, SyndeticsUnboundUtil } from "@tlc/syndetics-util";
import { useLanguage } from "./Language";

interface SearchSuggestionsProps {
  syndeticsClientCode: string;
  searchTerm: string;
  loading: boolean; // could loading be inferred by suggestions being set to null?
  suggestions: Suggestion[];
  selectedIndex: number;
  onClick: (index: number) => void;
  maxPerformedSearchSuggestions: number;
  maxAuthorSuggestions: number;
  maxTitleSuggestions: number;
}

// TODO cover component
// TODO search term highlighting?

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  syndeticsClientCode,
  searchTerm,
  suggestions,
  loading,
  selectedIndex,
  onClick,
  maxPerformedSearchSuggestions,
  maxAuthorSuggestions,
  maxTitleSuggestions,
}) => {
  const { translate } = useLanguage();
  const stringAvatar = (name: string) => {
    return name
      .split(", ")
      .reverse()
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const getCoverImage = ({
    localSmallImage,
    localLargeImage,
    shortAuthor,
    shortTitle,
    standardNumbers = [],
  }: TitleSuggestion) => {
    // TODO add fallthrough/retry
    // TODO add image dimension requests (width/height in pixels)

    if (localSmallImage) {
      return localSmallImage;
    }

    if (localLargeImage) {
      return localLargeImage;
    }

    if (syndeticsClientCode) {
      const isbns = standardNumbers
        .filter((value) => value.type.toUpperCase() === "ISBN")
        .map((isbn) => isbn.data);
      const upcs = standardNumbers
        .filter((value) => value.type.toUpperCase() === "UPC")
        .map((isbn) => isbn.data);
      return SyndeticsUnboundUtil.getCoverImage(
        syndeticsClientCode,
        isbns,
        shortTitle,
        CoverSize.SM,
        upcs,
        shortAuthor
      );
    }

    // TODO return what here?
  };

  return (
    <List
      sx={{
        position: "relative",
        overflow: "auto",
        maxHeight: "100vh",
        padding: 0,
        "& ul": { padding: 0 },
      }}
    >
      <ListItemButton
        onClick={() => onClick(0)}
        dense={true}
        selected={selectedIndex === 0}
      >
        <ListItemText inset primary={searchTerm} />
        <ListItemIcon>
          <SearchIcon />
        </ListItemIcon>
      </ListItemButton>

      {loading ? (
        <React.Fragment>
          {Array.from(
            { length: maxPerformedSearchSuggestions },
            (v, i) => i
          ).map((suggestionNumber) => (
            <ListItem key={`searchSuggestion${suggestionNumber}`} dense={true}>
              <ListItemText inset primary={<Skeleton />} />
              <ListItemIcon sx={{ ml: 1 }}>
                <Skeleton variant="circular" width={24} height={24} />
              </ListItemIcon>
            </ListItem>
          ))}
          {Array.from({ length: maxAuthorSuggestions }, (v, i) => i).map(
            (suggestionNumber) => (
              <ListItem
                key={`authorSuggestion${suggestionNumber}`}
                dense={true}
              >
                <ListItemAvatar>
                  <Skeleton variant="circular">
                    <Avatar />
                  </Skeleton>
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton />}
                  secondary={<Skeleton width={56} />}
                />
                <ListItemIcon sx={{ ml: 1 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
              </ListItem>
            )
          )}
          {Array.from({ length: maxTitleSuggestions }, (v, i) => i).map(
            (suggestionNumber) => (
              <ListItem key={`titleSuggestion${suggestionNumber}`} dense={true}>
                <ListItemAvatar>
                  <Skeleton variant="rectangular">
                    <Avatar />
                  </Skeleton>
                </ListItemAvatar>
                <ListItemText primary={<Skeleton />} secondary={<Skeleton />} />
                <ListItemIcon sx={{ ml: 1 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
              </ListItem>
            )
          )}
          {/* RSS feed */}
          {Array.from({ length: maxTitleSuggestions }, (v, i) => i).map(
            (suggestionNumber) => (
              <ListItem key={`rssSuggestion${suggestionNumber}`} dense={true}>
                <ListItemIcon>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
                <ListItemText
                  primary={<Skeleton />}
                  secondary={<Skeleton width={72} />}
                />
                <ListItemIcon sx={{ ml: 1 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
              </ListItem>
            )
          )}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {suggestions.map(({ type, value, key }, index) => {
            const offsetIndex = index + 1;
            switch (type) {
              case "author":
                const author = (value as AuthorSuggestion).displayAuthor;
                return (
                  <ListItemButton
                    key={key}
                    onClick={() => onClick(offsetIndex)}
                    selected={selectedIndex === offsetIndex}
                    dense={true}
                  >
                    <ListItemAvatar>
                      <Avatar>{stringAvatar(author)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={author}
                      secondary={translate("author")}
                    />
                    <ListItemIcon>
                      <SearchIcon />
                    </ListItemIcon>
                  </ListItemButton>
                );
              case "newsFeed":
                // TODO do these actually have local book covers?
                return (
                  <ListItemButton
                    key={key}
                    dense={true}
                    onClick={() => onClick(offsetIndex)}
                    selected={selectedIndex === offsetIndex}
                  >
                    <ListItemIcon>
                      <RssFeedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={(value as NewsFeedResource).shortTitle}
                      secondary={translate("rssFeedItem")}
                    />
                    <ListItemIcon>
                      <ArrowForwardIosIcon />
                    </ListItemIcon>
                  </ListItemButton>
                );
              case "performedSearch":
                return (
                  <ListItemButton
                    key={key}
                    dense={true}
                    onClick={() => onClick(offsetIndex)}
                    selected={selectedIndex === offsetIndex}
                  >
                    <ListItemText
                      inset
                      primary={(value as PerformedSearchSuggestion).term}
                    />
                    <ListItemIcon sx={{ ml: 1 }}>
                      <SearchIcon />
                    </ListItemIcon>
                  </ListItemButton>
                );
              case "title":
                const resource = value as TitleSuggestion;
                const avatarSrc = getCoverImage(resource);
                return (
                  <ListItemButton
                    key={key}
                    dense={true}
                    onClick={() => onClick(offsetIndex)}
                    selected={selectedIndex === offsetIndex}
                  >
                    <ListItemAvatar>
                      {avatarSrc ? (
                        <Avatar variant="square" src={avatarSrc} />
                      ) : (
                        <Skeleton variant="rectangular">
                          <Avatar />
                        </Skeleton>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={resource.shortTitle}
                      secondary={`${resource.format} \u2022 ${resource.shortAuthor}`}
                    />
                    <ListItemIcon>
                      <ArrowForwardIosIcon />
                    </ListItemIcon>
                  </ListItemButton>
                );
            }
          })}
        </React.Fragment>
      )}
    </List>
  );
};
